import { useCallback, useEffect, useRef, useState } from "react"

import moment from "moment"
import { useTranslation } from "react-i18next"
import { useSearchParams } from "react-router-dom"
import { Button, Divider, Icon, Image, Label, Popup } from "semantic-ui-react"
import { handleClickForKeyboard } from "simplydo/core/accessibility"
import type { OpenAPI } from "simplydo/interfaces"
import { Emoji } from "simplydo/react/components/Choosers/EmojiChooser/Emoji"
import { EmojiChooser } from "simplydo/react/components/Choosers/EmojiChooser/EmojiChooser"
import { GiphyGif } from "simplydo/react/components/Giphy/Gif"
import { styled } from "styled-components"

import UserChip from "components/lib/Chips/UserChip"
import ReportContentChip from "components/lib/ReportContent/ReportContentChip"
import { MultiLineParagraph } from "components/lib/UI"
import { useAppSelector } from "store"
import util from "utils/utils"

import { CommentInput, MediaContainer } from "./CommentInput"
import type { CommentState } from "./Discussion"

const CommentContainer = styled.div<{
  $isChild: boolean
  $isPinned: boolean
  $isHighlighted: boolean
  $requiresApproval: boolean
}>`
  display: flex;
  flex-direction: column;
  align-self: stretch;
  border-radius: 5px;
  background-color: ${({ $requiresApproval }) => ($requiresApproval ? "#f0f0f0" : "transparent")};
  padding: ${({ $requiresApproval }) => ($requiresApproval ? "8px" : "8px 0px 8px 8px")};
  margin-bottom: ${({ $requiresApproval }) => ($requiresApproval ? "8px" : "0")};

  margin-left: ${({ $isChild }) => ($isChild ? "-35px" : "0")};

  ${({ $isPinned }) =>
    $isPinned &&
    `
    padding: 10px;
    border-radius: 5px;
    background-color: #f6f6db;
    border: 2px solid #dadba0;
    margin-left: 0;
    margin-bottom: 15px;
  `}

  ${({ $isHighlighted }) =>
    $isHighlighted &&
    `
    padding: 10px;
    border-radius: 5px;
    background-color: #88e4d51c;
    border: 2px solid #5da2b3;
    margin-left: 0;
    margin-bottom: 15px;
  `}
`

const CollapsibleContainer = styled.div<{
  $collapsed: boolean
}>`
  display: ${({ $collapsed }) => ($collapsed ? "none" : "flex")};
  flex-direction: column;
  align-items: flex-start;
  flex: 1;
  padding-left: 55px;
`

const ContentContainer = styled.div`
  display: flex;
  flex: 1;
  width: 100%;
  flex-direction: column;

  .flag-span {
    visibility: ${({ theme }) => (theme.sizes.isComputer ? "hidden" : "visible")};
  }
  &:hover .flag-span {
    visibility: visible;
  }
`

const AuthorContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-top: 5px;
  margin-bottom: 3.5px;
  a {
    font-weight: 700;
    font-size: 0.9em;
    color: black;
    display: block;
    line-height: 1.2;
  }
`

const TextContainer = styled.div<{
  $requiresApproval: boolean
}>`
  p {
    color: ${({ $requiresApproval }) => ($requiresApproval ? "grey" : "#2d2d2d")};
  }
`

const ActionsContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  margin: 3.5px 0 7.5px;
  justify-content: space-between;
  width: 100%;
  span {
    display: block;
    color: gray;
    font-weight: 550;
    cursor: pointer;
    margin-right: 7.5px;
    font-size: 0.85em;
  }
`

const CollapseIcon = styled(Icon)<{ $collapsed: boolean }>`
  cursor: pointer;
  && {
    opacity: 0.5;
    margin-top: ${({ $collapsed }) => ($collapsed ? "12px" : "15px")};

    transition: transform 0.2s;
    ${({ $collapsed }) => !$collapsed && "transform: rotate(90deg);"}

    &:hover {
      opacity: 1;
    }
  }
`

const EmojiLabel = styled(Label)`
  && {
    padding: 2px 4px 1px 4px;
    max-width: none !important;
    cursor: pointer;

    &:hover {
      filter: brightness(1.05);
    }
  }
`

const NewEmojiLabel = styled(Label)`
  cursor: pointer;

  opacity: 0.5;
  transition: opacity 0.2s;
  filter: grayscale(100%);
  &:hover {
    opacity: 1;
    filter: grayscale(0%);
  }
`

type CommentType = OpenAPI.GET<"/discussions/{discussion_id}/comments">["response"]["comments"][0]

type DiscussionCommentProps = {
  discussionId: string
  comment: CommentType
  replies: CommentType[]
  currentReply: CommentState[string]
  postComment: () => void
  canManage?: boolean

  updateReply?: (
    parentCommentId: string,
    commentOrUpdater: CommentState[string] | ((prevState: CommentState[string]) => CommentState[string]),
  ) => void
  deleteComment?: (commentId: string) => void
  approveComment?: (commentId: string) => void
  rejectComment?: (commentId: string) => void
  moderationEnabled?: boolean
  deleteAttachment?: (index: number, commentId: string) => void
  pinComment?: (commentId: string) => void
  unpinComment?: (commentId: string) => void
  reactComment?: (commentId: string, emoji: string, react: boolean) => void
  forType: string
  forId: string
  onNestedReply?: (comment) => void
}

const DisscussionComment = ({
  discussionId,
  forType,
  forId,
  comment,
  replies,
  currentReply,
  postComment,
  canManage,
  updateReply,
  deleteComment,
  approveComment,
  rejectComment,
  moderationEnabled,
  pinComment,
  unpinComment,
  reactComment,
  onNestedReply,
}: DiscussionCommentProps) => {
  const [collapsed, setCollapsed] = useState(false)
  const [replyBoxOpen, setReplyBoxOpen] = useState(false)
  const inputRef = useRef()

  const { t } = useTranslation()
  const user = useAppSelector((state) => state.user)

  const [searchParams] = useSearchParams()
  const paramComment = searchParams.get("comment")
  const commentIsHighlighted = paramComment === comment._id

  const canPostNewComments = util.hasPermission(user, "discussion.postNewComments", discussionId, forId)
  const canPostReplies = util.hasPermission(user, "discussion.postReplies", discussionId, forId)
  const canPinComments = util.hasPermission(user, "discussion.pinComments", discussionId, forId)
  const canDeleteComments = util.hasPermission(user, "discussion.deleteComments", discussionId, forId)
  const canModerateComments = util.hasPermission(user, "discussion.moderateComments", discussionId, forId)
  const canPostMedia = util.hasPermission(user, "discussion.postMedia", discussionId, forId)
  const canUseReacts = util.hasPermission(user, "discussion.addReacts", discussionId, forId)

  useEffect(() => {
    if (commentIsHighlighted) {
      const commentElement = document.getElementById(paramComment)
      if (commentElement) {
        commentElement.scrollIntoView({ behavior: "smooth", block: "center" })
      }
    }
  }, [paramComment, commentIsHighlighted])

  const handleCollapsing = () => {
    setCollapsed((prev) => !prev)
  }

  const postReply = () => {
    postComment()
    setReplyBoxOpen(false)
    setCollapsed(false)
  }

  const replyToExistingReply = useCallback(
    (user: OpenAPI.Schemas["User"]) => {
      /*
      - The comment box uses rich text to allow various mention compatibility, however the replyText is a plain string
      - The comment box thus manages it's own state, and sends onChanges back here when the HTML changes
      - This means we can't just run updateComment(), because it won't update the reply box
      - Therefore we supply a special insertion string: <?insert!>
      - The Mention box will recognise this special string, and know to insert this text to it's HTML, while removing the special string itself
      - We also need to add the mention as HTML, rather than the standard "(@)[Name](_id:id_here)" format
    */
      setTimeout(() => {
        const newComment = `(@)[${user.profile.fullName}](_id:${user._id}) <?insert!>`
        updateReply(comment._id, (prev) => ({ ...prev, message: newComment }))
      }, 0)
    },
    [comment._id, updateReply],
  )

  const reacts: Array<{
    emoji: string
    count: number
    users: OpenAPI.Schemas["User"][]
    hasReacted: boolean
  }> = []
  comment.reacts?.forEach((react) => {
    let index = reacts.map((r) => r.emoji).indexOf(react.emoji)
    if (index === -1) {
      reacts.push({
        ...react,
        count: 0,
        users: [],
        hasReacted: false,
      })
      index = reacts.length - 1
    }
    reacts[index].users.push(react.ownerUser)
    reacts[index].count += 1
    if (react.user === user._id) reacts[index].hasReacted = true
  })

  return (
    <CommentContainer
      id={comment._id}
      key={comment._id}
      $isPinned={comment.isPinned}
      $isChild={!!comment.parent}
      $isHighlighted={commentIsHighlighted}
      $requiresApproval={moderationEnabled && comment.requiresApproval}
    >
      <div>
        {comment.isPinned ? (
          <Popup
            content="This comment is pinned"
            trigger={
              <Label size="small" color="yellow">
                <Icon name="pin" style={{ margin: 0 }} /> Pinned
              </Label>
            }
          />
        ) : null}
      </div>

      {moderationEnabled && comment.requiresApproval ? (
        <>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              paddingTop: 0,
              paddingBottom: 7,
            }}
          >
            <p style={{ marginBottom: 0 }}>
              <Icon name="exclamation triangle" color="yellow" style={{ marginLeft: 2, marginRight: 6 }} />
              This post is <strong>awaiting approval</strong>.
            </p>
            {canModerateComments || canManage ? (
              <div style={{ display: "flex" }}>
                <Button
                  size="mini"
                  basic
                  content="Approve"
                  icon="checkmark"
                  onClick={() => approveComment(comment._id)}
                />
                <Button size="mini" basic icon="trash" onClick={() => rejectComment(comment._id)} />
              </div>
            ) : null}
          </div>
          <Divider fitted style={{ marginBottom: 3 }} />
        </>
      ) : null}

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div style={{ display: "flex" }}>
          <CollapseIcon $collapsed={collapsed} name="chevron right" onClick={handleCollapsing} />
          <AuthorContainer>
            <UserChip user={comment.ownerUser} compact showSocials />
          </AuthorContainer>
        </div>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end" }}>
          <span
            style={{
              fontSize: "0.9em",
              color: "gray",
              lineHeight: 1.2,
            }}
          >
            {moment(comment.createdAt).fromNow()}
          </span>
          {(canModerateComments || canManage) && comment?.approvedBy ? (
            <span style={{ fontSize: "0.7em", color: "gray" }}>
              Approved {comment.approvedAt ? moment(comment.approvedAt).format("MMM D") : "Unknown time"} by{" "}
              <UserChip compact="very" user={comment.ownerApprovedBy} />
            </span>
          ) : null}
        </div>
      </div>
      <CollapsibleContainer $collapsed={collapsed}>
        <ContentContainer>
          <TextContainer $requiresApproval={moderationEnabled && comment.requiresApproval}>
            <MultiLineParagraph
              mentionedUsers={comment.mentionedUsers}
              mentionedUserAccess={comment.mentionedUserAccess}
              content={comment.message || ""}
            />
          </TextContainer>
          {comment.media?.length ? (
            <MediaContainer>
              {comment.media.map((media) => {
                if (media.type === "image") {
                  return (
                    <div
                      key={media.value}
                      style={
                        comment.media.length > 1
                          ? {
                              minWidth: 100,
                              maxWidth: 200,
                              height: 150,
                              maxHeight: 150,
                              overflow: "hidden",
                            }
                          : { width: "100%" }
                      }
                    >
                      <a
                        href={media.signedDownloadRequest}
                        rel="noopener noreferrer"
                        target="_blank"
                        style={{ display: "inline-block" }}
                      >
                        <Image
                          rounded
                          bordered
                          style={{
                            objectFit: "contain",
                            maxHeight: 150,
                          }}
                          alt="Comment attachment"
                          className="comment-img"
                          src={media.signedDownloadRequest}
                        />
                      </a>
                    </div>
                  )
                }
                if (media.type === "gif") {
                  return <GiphyGif style={{ borderRadius: 5 }} key={media.value} gif={media.data} size="fixed" />
                }
                return (
                  <a
                    href={media.signedDownloadRequest || media.value}
                    key={media.value}
                    target="_blank"
                    rel="noopener noreferrer"
                    download={media.name}
                    className="ui tiny basic button secondary"
                    style={{
                      borderRadius: 5,
                      maxWidth: 200,
                      textWrap: "nowrap",
                      overflow: "hidden",
                      display: "block",
                      textOverflow: "ellipsis",
                    }}
                  >
                    <i className="paperclip icon"></i>
                    {media.name}
                  </a>
                )
              })}
            </MediaContainer>
          ) : null}

          {(!moderationEnabled || !comment.requiresApproval) && (canUseReacts || canManage) ? (
            <MediaContainer>
              {reacts?.map((react) => (
                <Popup
                  key={react.emoji}
                  clickable="true"
                  trigger={
                    <EmojiLabel
                      size="tiny"
                      onClick={() => reactComment(comment._id, react.emoji, !react.hasReacted)}
                      color={react.hasReacted ? "yellow" : "grey"}
                    >
                      <Emoji style={{ display: "inline-block" }} emoji={{ id: react.emoji }} size={14} />
                      <span
                        style={{
                          verticalAlign: "top",
                          display: "inline-block",
                          marginLeft: 5,
                          marginTop: 2,
                        }}
                      >
                        {react.count}
                      </span>
                    </EmojiLabel>
                  }
                  content={
                    <p style={{ fontSize: 11 }}>
                      {`${react?.users?.map((u) => (u?._id === user._id ? "You" : u?.profile?.fullName || "Someone")).join(" and ")} reacted with :${react?.emoji}:`}
                    </p>
                  }
                />
              ))}
              {canPostNewComments || canManage ? (
                <Popup
                  flowing
                  on="click"
                  basic
                  size="tiny"
                  position="top right"
                  trigger={
                    <NewEmojiLabel
                      size="tiny"
                      style={{
                        padding: "2px 4px 1px 4px",
                        maxWidth: "none",
                        cursor: "pointer",
                      }}
                      color={"grey"}
                    >
                      <Emoji style={{ display: "inline-block" }} emoji={{ id: "grinning" }} size={14} />
                      <Icon
                        name="plus"
                        style={{
                          verticalAlign: "top",
                          marginRight: 0,
                          marginLeft: 4,
                          marginTop: 2,
                        }}
                      />
                    </NewEmojiLabel>
                  }
                  content={<EmojiChooser onComplete={(em) => reactComment(comment._id, em, true)} />}
                />
              ) : null}
            </MediaContainer>
          ) : null}

          <ActionsContainer>
            <div style={{ display: "flex", alignItems: "center" }}>
              {(!moderationEnabled || !comment.requiresApproval) && (canPostReplies || canManage) ? (
                <span
                  role="button"
                  tabIndex={0}
                  onClick={() => {
                    if (onNestedReply) {
                      onNestedReply(comment)
                      return
                    }

                    setReplyBoxOpen(true)
                    setTimeout(() => {
                      // @ts-ignore
                      inputRef.current?.focus(100000)
                    }, 150)
                  }}
                  onKeyDown={handleClickForKeyboard(() => {
                    if (onNestedReply) {
                      onNestedReply(comment)
                      return
                    }

                    setReplyBoxOpen(true)
                    setTimeout(() => {
                      // @ts-ignore
                      inputRef.current?.focus(100000)
                    }, 150)
                  })}
                >
                  {t("feeds.comments.reply")}
                </span>
              ) : null}

              {comment.user === user._id || canDeleteComments || canManage ? (
                <span role="button" tabIndex={0} onClick={() => deleteComment(comment._id)}>
                  {t("generic.delete")}
                </span>
              ) : null}

              {(!moderationEnabled || !comment.requiresApproval) && (canPinComments || canManage) && !comment.parent ? (
                comment.isPinned ? (
                  <span role="button" tabIndex={0} onClick={() => unpinComment(comment._id)}>
                    {t("feeds.pin.unPin")}
                  </span>
                ) : (
                  <span role="button" tabIndex={0} onClick={() => pinComment(comment._id)}>
                    {t("feeds.pin.pin")}
                  </span>
                )
              ) : null}
            </div>
            <div className="flag-span">
              <ReportContentChip forType="comment" forId={comment._id} />
            </div>
          </ActionsContainer>
        </ContentContainer>

        {replies.map((reply) => (
          <DisscussionComment
            key={reply._id}
            discussionId={discussionId}
            forType={forType}
            forId={forId}
            comment={reply}
            replies={[]}
            currentReply={currentReply}
            postComment={postComment}
            canManage={canManage}
            updateReply={updateReply}
            deleteComment={deleteComment}
            approveComment={approveComment}
            rejectComment={rejectComment}
            moderationEnabled={moderationEnabled}
            reactComment={reactComment}
            onNestedReply={(replyComment) => {
              setReplyBoxOpen(true)
              if (replyComment.parent) {
                replyToExistingReply(replyComment.ownerUser)
              }
              setTimeout(() => {
                // @ts-ignore
                inputRef.current?.focus(100000)
              }, 150)
            }}
          />
        ))}

        {replyBoxOpen && (
          <div style={{ flex: 1, width: "100%" }}>
            <CommentInput
              inputRef={inputRef}
              forType={forType}
              forId={forId}
              discussionId={discussionId}
              currentReply={currentReply}
              updateReply={(commentOrUpdater) => updateReply(comment.parent || comment._id, commentOrUpdater)}
              postComment={postReply}
              canPostMedia={canManage || canPostMedia}
            />
          </div>
        )}
      </CollapsibleContainer>
    </CommentContainer>
  )
}

export default DisscussionComment
